// Create: src/utils/tokenHelper.ts

import axios from "axios";
import CryptoJS from "crypto-js";
import CONFIG from "../config";

/**
 * Fetches token from tokenUrl if channel requires it
 * @param {Object} channel - Channel object with token and tokenUrl fields
 * @returns {Promise<string|null>} - Returns token string or null if not needed
 */
export async function fetchChannelToken(channel: any): Promise<string | null> {
  // Check if token is required
  if (!channel.token || channel.token !== true) {
    console.log("🔓 Channel does not require token");
    return null;
  }

  // Check if tokenUrl exists
  if (!channel.tokenUrl) {
    console.warn("⚠️ Channel requires token but tokenUrl is missing");
    return null;
  }

  console.log("🔐 Fetching token from:", channel.tokenUrl);

  try {
    // Generate auth header
    const authHeader = generateAuthHeader();

    // Make API call to tokenUrl
    const response = await axios.get(channel.tokenUrl, {
      headers: {
        auth: authHeader,
      },
      timeout: 8000,
    });

    console.log("✅ Token API Response:", response.data);

    // Extract extra_data which contains the token or additional info
    if (response.data && response.data.extra_data) {
      const extraData = response.data.extra_data;
      console.log(
        "🎟️ Token/Extra data fetched successfully:",
        extraData.substring(0, 50) + "..."
      );
      return extraData;
    }

    console.warn("⚠️ No extra_data in response:", response.data);
    return null;
  } catch (error: any) {
    console.error("❌ Error fetching token:", error.message);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
    }
    return null;
  }
}

/**
 * Fetches token from tokenUrl for a movie/episode if it requires one.
 * Same contract as fetchChannelToken, except the backend expects a POST here.
 * @param {Object} movie - Movie/episode object with token and tokenUrl fields
 * @returns {Promise<string|null>} - Returns the extra_data query string (e.g. "?coid=1&token=...") or null
 */
export async function fetchMovieToken(movie: any): Promise<string | null> {
  // Check if token is required
  if (!movie.token || movie.token !== true) {
    console.log("🔓 Movie does not require token");
    return null;
  }

  // Check if tokenUrl exists
  if (!movie.tokenUrl) {
    console.warn("⚠️ Movie requires token but tokenUrl is missing");
    return null;
  }

  console.log("🔐 Fetching movie token from:", movie.tokenUrl);

  try {
    // Generate auth header
    const authHeader = generateAuthHeader();

    // Make API call to tokenUrl — movies use POST, unlike live channels
    const response = await axios.post(
      movie.tokenUrl,
      undefined,
      {
        headers: {
          auth: authHeader,
        },
        timeout: 8000,
      }
    );

    console.log("✅ Movie token API Response:", response.data);

    // Extract extra_data which contains the query string to append to direct_source
    if (response.data && response.data.extra_data) {
      const extraData = response.data.extra_data;
      console.log(
        "🎟️ Movie token/extra data fetched successfully:",
        extraData.substring(0, 50) + "..."
      );
      return extraData;
    }

    console.warn("⚠️ No extra_data in response:", response.data);
    return null;
  } catch (error: any) {
    console.error("❌ Error fetching movie token:", error.message);
    if (error.response) {
      console.error("Response data:", error.response.data);
      console.error("Response status:", error.response.status);
    }
    return null;
  }
}

/**
 * Builds the final movie stream URL by appending the token's extra_data
 * (a query string, e.g. "?coid=1&token=...") onto the movie's direct_source.
 * @param {string} directSource - The movie's base CDN URL
 * @param {string|null} tokenData - The extra_data query string returned by fetchMovieToken
 * @returns {string} - The complete playable URL
 */
export function buildMovieStreamUrl(
  directSource: string,
  tokenData: string | null
): string {
  if (!tokenData) {
    console.log("🔗 Using direct_source (no token):", directSource);
    return directSource;
  }

  let extra = tokenData;
  // If direct_source already has a query string, turn a leading "?" into "&"
  if (directSource.includes("?") && extra.startsWith("?")) {
    extra = "&" + extra.slice(1);
  } else if (!directSource.includes("?") && !extra.startsWith("?")) {
    extra = "?" + extra;
  }

  const finalUrl = directSource + extra;
  console.log("🔗 Final movie URL with token:", finalUrl.substring(0, 150) + "...");
  return finalUrl;
}

// Even simpler approach - Update buildStreamUrl:

export function buildStreamUrl(
  channel: any,
  baseUrl: string,
  tokenData: string | null
): string {
  // If we have token data, it contains the complete URL with token
  if (tokenData) {
    let finalUrl = tokenData;

    console.log("🔗 Raw token data:", tokenData.substring(0, 150) + "...");

    // ✅ Clean up the URL
    // Remove any leading '?' character
    finalUrl = finalUrl.replace(/^\?+/, "");

    // Add protocol if missing
    if (finalUrl.startsWith("//")) {
      finalUrl = "https:" + finalUrl;
    } else if (!finalUrl.startsWith("http")) {
      finalUrl = "https://" + finalUrl;
    }

    console.log("🔗 Final URL with token:", finalUrl.substring(0, 150) + "...");
    return finalUrl;
  }

  // No token required, use the base URL
  console.log("🔗 Using base stream URL (no token):", baseUrl);
  return baseUrl;
}

/**
 * Generates auth header for token API call
 */
function generateAuthHeader(): string {
  const authObject = {
    auth: generateAuth(),
    devicebrand: CONFIG.postdata.devicebrand,
    app_name: CONFIG.postdata.app_name,
    appversion: CONFIG.postdata.appversion,
    hdmi: CONFIG.postdata.hdmi,
    api_version: CONFIG.postdata.api_version,
    language: CONFIG.postdata.language,
    device_timezone: CONFIG.postdata.device_timezone,
    ntype: CONFIG.postdata.ntype,
    macaddress: CONFIG.postdata.macaddress,
    os: CONFIG.postdata.os,
    screensize: CONFIG.postdata.screensize,
    firmwareversion: CONFIG.postdata.firmwareversion,
    company_id: CONFIG.postdata.company_id,
  };

  return encodeURIComponent(
    JSON.stringify(authObject)
      .replace("{", "")
      .replace("}", "")
      .replace(/:/g, "=")
      .replace(/"/g, "")
  );
}

/**
 * Generates encrypted auth string
 */
function generateAuth(): string {
  const plainText = `username=${CONFIG.authobject.username};password=${CONFIG.authobject.password};appid=${CONFIG.authobject.appid};company_id=${CONFIG.authobject.company_id};boxid=${CONFIG.authobject.boxid};timestamp=${Date.now()}`;

  return encrypt(plainText, CONFIG.authobject.encryption_key);
}

/**
 * Encrypts plaintext with AES encryption
 */
function encrypt(plainText: string, key: string): string {
  const C = CryptoJS;
  const plainTextParsed = C.enc.Utf8.parse(plainText);
  const keyParsed = C.enc.Utf8.parse(key);

  const aes = C.algo.AES.createEncryptor(keyParsed, {
    mode: C.mode.CBC,
    padding: C.pad.Pkcs7,
    iv: keyParsed,
  });

  const encrypted = aes.finalize(plainTextParsed);
  return C.enc.Base64.stringify(encrypted);
}

/**
 * Builds stream URL with token if required
 * @param {string} baseUrl - Base stream URL
 * @param {string|null} token - Token string or null
 * @returns {string} - Final stream URL with token appended if needed
 */
export function buildStreamUrlWithToken(
  baseUrl: string,
  token: string | null
): string {
  if (!token) {
    console.log("🔗 Stream URL (no token):", baseUrl);
    return baseUrl;
  }

  // Append token as query parameter
  const separator = baseUrl.includes("?") ? "&" : "?";
  const finalUrl = `${baseUrl}${separator}token=${encodeURIComponent(token)}`;

  console.log(
    "🔗 Stream URL (with token):",
    finalUrl.substring(0, 100) + "..."
  );
  return finalUrl;
}
